// External Imports
import { Fragment, useState } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import Modal from "../UI/Modal";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import LoadingSpinner from "../UI/LoadingSpinner";
import { requestAdminAPI } from "../../api/posh-api";
import SelectPair from "../UI/SelectPair";

/** ========================================================================
 * * RefundModal Component
 * This component renders a form to refund a payment.
 * ========================================================================== */

export default function RefundModal({ open, setOpen, payment, onComplete }) {
  const [loading, setLoading] = useState(false);

  /* ====================== REFUND PAYMENT ====================== */
  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        paymentId: payment.id,
        amount: parseFloat(e.target.refund.value),
        reason: e.target.reason.value,
        process: e.target.process?.value === "Yes",
      };
      await requestAdminAPI("refund-payment", payload);
      onComplete && onComplete();
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  if (!payment) return null;
  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:w-[45vw]">
        <Heading
          title="Refund Payment"
          text="If the payment was made via Stripe, the process refund can option can be used to refund the amount to the customer's payment method.
          Otherwise if the payment was marked as paid, the record will be updated to reflect the refund."
          isBackArrow={false}
          id={payment?.id}
        >
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>
        <form onSubmit={submitHandler} className="info lg:grid-cols-2">
          <DetailPair
            label="Customer"
            value={`${payment.customer?.firstName} ${payment.customer?.lastName}`}
          />
          <DetailPair
            label="Booking"
            value={
              payment.booking
                ? `${payment.booking?.vehicle.year} ${payment.booking?.vehicle?.make?.name} ${payment.booking?.vehicle?.model}`
                : "-"
            }
          />
          <div className="lg:col-span-2">
            <DetailPair label="Description" value={payment.description} />
          </div>
          <div className="grid gap-4 lg:grid-cols-3 lg:col-span-2">
            <DetailPair
              label="Amount Paid"
              classes="font-semibold text-xl"
              value={`$${payment.amount?.toFixed(2)}`}
            />
            <DetailPair
              label="Refund Amount"
              edit={true}
              inputOptions={{
                required: true,
                defaultValue: "0",
                type: "number",
                step: "0.01",
                min: "0",
                name: "refund",
              }}
            />
            {payment.stripeId && (
              <SelectPair
                label="Process Refund"
                options={["No", "Yes"]}
                edit={true}
                inputOptions={{
                  required: true,
                  defaultValue: "No",
                  name: "process",
                }}
              />
            )}
          </div>
          <div className="lg:col-span-2">
            <DetailPair
              label="Refund Reason"
              edit={true}
              inputOptions={{
                required: true,
                name: "reason",
                placeholder: "Why is this payment being refunded?",
              }}
            />
          </div>
          {loading ? (
            <div className="mx-auto lg:col-span-2">
              <LoadingSpinner />
            </div>
          ) : (
            <Fragment>
              <div onClick={() => setOpen(false)} className="btn-secondary">
                Cancel
              </div>
              <button type="submit" className="btn-primary">
                Refund Payment
              </button>
            </Fragment>
          )}
        </form>
      </div>
    </Modal>
  );
}
